import { Component, useEffect, useRef, type ReactNode } from "react";

type Feature = "application" | "page" | "tasks" | "trade" | "liquidity" | "bridge" | "create" | "explore" | "token";
type Category = "PRESENTATION_FAILURE" | "CHUNK_LOAD_FAILURE";

// Deliberately exclude the thrown error, stack, URL and model/wallet data.
export function reportPresentationFailure(feature: Feature, category: Category = "PRESENTATION_FAILURE") {
  console.error("Frontend presentation recovery", { category, feature, buildMode: import.meta.env.MODE });
}

export function RecoveryNotice({ title, reload = false, onRetry, children }: {
  title: string; reload?: boolean; onRetry?: () => void; children?: ReactNode;
}) {
  const focusTarget = useRef<HTMLElement>(null);
  useEffect(() => { focusTarget.current?.focus(); }, []);
  return <main ref={focusTarget} id="main-content" className="app-frame" tabIndex={-1} aria-label="Page recovery">
    <section role="alert" className="recovery-notice">
      <h1>{title}</h1>
      <p>{reload ? "Reload to try again. A wallet request already approved may still complete. Check wallet activity and keep any transaction hash before reloading." :
        "The page could not be displayed. Your current mining state and transaction tracking are retained. Restore the page to view their current state."}</p>
      <p>Recovery does not resend a transaction or restart mining.</p>
      <button type="button" className="secondary-action" onClick={reload ? () => window.location.reload() : onRetry}>
        {reload ? "Reload page" : "Restore page"}
      </button>
      {children}
    </section>
  </main>;
}

interface BoundaryProps {
  feature: "application" | "page";
  resetKey?: string;
  children: ReactNode;
}

export class PresentationBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { reportPresentationFailure(this.props.feature); }
  componentDidUpdate(previous: BoundaryProps) {
    // Do not key/remount healthy pages on wallet context changes.
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return <RecoveryNotice title="The terminal needs recovery" reload={this.props.feature === "application"}
      onRetry={() => this.setState({ failed: false })} />;
  }
}
